'use client'

import Image from 'next/image'
import { useSession } from 'next-auth/react'
import { useUser } from '@/contexts/UserContext'
import { useClientTranslation } from '@/hooks/useClientTranslation'
import { LanguageSelector } from '@/components/ui/LanguageSelector'
import { VersionedLink } from '@/components/VersionedLink'

export default function Home() {
  const { data: session, status } = useSession()
  const { platformName } = useUser()
  const { t } = useClientTranslation('common')

  // 如果正在加载session状态
  if (status === 'loading') {
    return (
      <main className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('home.loading')}</p>
        </div>
      </main>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      {/* 语言切换按钮 */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector iconOnly />
      </div>

      {/* 主要内容区域 */}
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            {/* Logo和平台名称 */}
            <div className="flex flex-col items-center mb-12">
              <div className="mb-6">
                <Image 
                  src="/logo.png" 
                  alt="Logo" 
                  width={180} 
                  height={180} 
                  className="object-contain"
                  priority
                />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {platformName}
              </h1>
              <p className="text-xl text-gray-600 mt-4 max-w-2xl">
                {t('home.tagline')}
              </p>
            </div>
            {/* 行动按钮 */}
            <div className="flex justify-center mb-16 space-x-4">
              {status === 'authenticated' ? (
                <>
                  <VersionedLink 
                    href={
                      session?.user?.role === 'DEVELOPER' ? '/developer' :
                      session?.user?.role === 'ADMIN' || session?.user?.role === 'SUPER_ADMIN' || session?.user?.role === 'OBSERVER' || session?.user?.role === 'AUDITOR' ? '/admin' : 
                      '/dashboard'
                    }
                    className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-4 px-10 rounded-xl transition-all text-center shadow-lg hover:shadow-xl transform hover:scale-105 text-lg"
                  >
                    {t('home.enterDashboard')}
                  </VersionedLink>
                </>
              ) : (
                <VersionedLink 
                  href="/auth/signin"
                  className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-4 px-10 rounded-xl transition-all text-center shadow-lg hover:shadow-xl transform hover:scale-105 text-lg"
                >
                  {t('home.startJourney')}
                </VersionedLink>
              )}
            </div>

            {/* 功能特性展示 */}
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-200 text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('home.features.insightful.title')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('home.features.insightful.description')}</p>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-green-200 text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('home.features.strategic.title')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('home.features.strategic.description')}</p>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-purple-200 text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('home.features.secure.title')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('home.features.secure.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* 固定在底部的Footer */}
      <footer className="py-8 mt-auto">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between max-w-4xl mx-auto">
            <div className="flex items-center space-x-3 mb-4 md:mb-0 opacity-60">
              <Image 
                src="/logo.png" 
                alt="Logo" 
                width={32} 
                height={32} 
                className="object-contain"
              />
              <span className="font-medium text-gray-600">{platformName}</span>
            </div>
            <div className="text-gray-400 text-sm">
              {t('home.footer')}
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}